/**
 * Retry utility for API calls with different strategies
 */

export const retryApiCall = async (apiFunction, options = {}) => {
    const {
        maxRetries = 3,
        baseDelayMs = 1000,
        silent = false,
        nonRetryableStatuses = [401, 403]
    } = options;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const result = await apiFunction();
            return result;
        } catch (error) {
            // Don't retry for authentication errors
            if (error.response?.status && nonRetryableStatuses.includes(error.response.status)) {
                throw error;
            }

            // Don't retry for client errors (4xx except 401, 403)
            if (error.response?.status >= 400 && error.response?.status < 500) {
                throw error;
            }

            // Final attempt failed
            if (attempt === maxRetries) {
                if (!silent) {
                    console.error(`API call failed after ${maxRetries} attempts:`, error);
                }
                throw error;
            }

            // Calculate delay with exponential backoff
            const delay = baseDelayMs * Math.pow(2, attempt - 1);
            
            if (!silent) {
                console.warn(`API call failed (attempt ${attempt}/${maxRetries}), retrying in ${delay}ms...`, error.message);
            }

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

/**
 * Retry configuration for different use cases
 */
export const RETRY_CONFIGS = {
    // Polling functions - silent failures, fewer retries
    POLLING: {
        maxRetries: 2,
        baseDelayMs: 1000,
        silent: true
    },
    
    // Initial load functions - show notifications, more retries
    INITIAL_LOAD: {
        maxRetries: 3,
        baseDelayMs: 1000,
        silent: false
    },
    
    // User action functions - moderate retries, show errors
    USER_ACTION: {
        maxRetries: 2,
        baseDelayMs: 1000,
        silent: false
    }
};

/**
 * Helper function to check if error is a network/connection error
 */
export const isNetworkError = (error) => {
    return (
        error.code === 'NETWORK_ERROR' ||
        error.message === 'Network Error' ||
        error.message.includes('timeout') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ENOTFOUND') ||
        !error.response // No response means network issue
    );
};

/**
 * Helper function to check if error indicates server is down
 */
export const isServerDown = (error) => {
    return (
        isNetworkError(error) ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ENOTFOUND' ||
        error.response?.status >= 500 ||
        error.message.includes('timeout')
    );
};
